import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    TextField,
    MenuItem,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Tooltip,
    LinearProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    InputAdornment,
    useMediaQuery,
    useTheme,
    Stack,
    Avatar,
    Divider,
    Fade,
    Slide,
    Badge,
    Autocomplete,
    CircularProgress,
} from '@mui/material';
import {
    Edit,
    Delete,
    Visibility,
    Add,
    Refresh,
    Search,
    Clear,
    Description,
    PictureAsPdf,
    FileDownload,
    Close,
    Restaurant,
    DirectionsCar,
    House,
    Bolt,
    Movie,
    ShoppingBag,
    MedicalServices,
    School,
    Shield,
    ReceiptLong,
    Flight,
    CardGiftcard,
    Subscriptions,
    MoreHoriz,
    Phone,
    Receipt,
    Store,
    SwapHoriz,
    Wifi,
    People,
    Link as LinkIcon,
    LinkOff as LinkOffIcon,
    PhoneAndroid,
    AccountBalance,
    Edit as EditIcon,
} from '@mui/icons-material';
import { format as formatDateFns } from 'date-fns';
import toast from 'react-hot-toast';
import expenseApi from '../api/expenseApi';
import ExportUtils from '../utils/exportUtils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Complete expense categories matching Flutter enum
const EXPENSE_CATEGORIES = [
    { value: 'food', label: 'Food & Dining', icon: <Restaurant />, color: '#FF9800' },
    { value: 'transportation', label: 'Transportation', icon: <DirectionsCar />, color: '#2196F3' },
    { value: 'housing', label: 'Housing', icon: <House />, color: '#795548' },
    { value: 'utilities', label: 'Utilities', icon: <Bolt />, color: '#FDD835' },
    { value: 'entertainment', label: 'Entertainment', icon: <Movie />, color: '#9C27B0' },
    { value: 'shopping', label: 'Shopping', icon: <ShoppingBag />, color: '#E91E63' },
    { value: 'healthcare', label: 'Healthcare', icon: <MedicalServices />, color: '#F44336' },
    { value: 'education', label: 'Education', icon: <School />, color: '#3F51B5' },
    { value: 'insurance', label: 'Insurance', icon: <Shield />, color: '#009688' },
    { value: 'taxes', label: 'Taxes', icon: <ReceiptLong />, color: '#9E9E9E' },
    { value: 'travel', label: 'Travel', icon: <Flight />, color: '#00BCD4' },
    { value: 'gifts', label: 'Gifts & Donations', icon: <CardGiftcard />, color: '#FF5722' },
    { value: 'subscriptions', label: 'Subscriptions', icon: <Subscriptions />, color: '#03A9F4' },
    { value: 'other', label: 'Other', icon: <MoreHoriz />, color: '#9E9E9E' },
    { value: 'airtime', label: 'Airtime', icon: <Phone />, color: '#4CAF50' },
    { value: 'paybill', label: 'PayBill', icon: <Receipt />, color: '#FF6F00' },
    { value: 'merchant_payment', label: 'Merchant Payment', icon: <Store />, color: '#7B1FA2' },
    { value: 'transfer', label: 'Transfer', icon: <SwapHoriz />, color: '#0288D1' },
    { value: 'internet_data', label: 'Internet & Data', icon: <Wifi />, color: '#00BCD4' },
    { value: 'customer_payment', label: 'Customer Payment', icon: <People />, color: '#388E3C' },
    { value: 'service_charges', label: 'Service Charges', icon: <ReceiptLong />, color: '#F57C00' },
];

// Source options for filtering
const SOURCE_OPTIONS = [
    { value: 'all', label: 'All Sources', icon: <MoreHoriz />, color: '#9E9E9E' },
    { value: 'mpesa', label: 'M-Pesa', icon: <PhoneAndroid />, color: '#4CAF50' },
    { value: 'bank', label: 'Bank', icon: <AccountBalance />, color: '#1976D2' },
    { value: 'manual', label: 'Manual', icon: <EditIcon />, color: '#FF9800' },
];

// Get category details by value
const getCategoryDetails = (value) => {
    const category = EXPENSE_CATEGORIES.find(cat => cat.value === value);
    return category || EXPENSE_CATEGORIES.find(cat => cat.value === 'other');
};

// Get source details by value
const getSourceDetails = (value) => {
    const source = SOURCE_OPTIONS.find(src => src.value === value);
    return source || SOURCE_OPTIONS[0];
};

const ExpenseList = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
    const [total, setTotal] = useState(0);
    const [filteredExpenses, setFilteredExpenses] = useState([]);
    const userId = expenseApi.getCurrentUserId();

    // State for linking expenses to transactions
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientTransactions, setClientTransactions] = useState([]);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkingExpense, setLinkingExpense] = useState(null);
    const [linkingLoading, setLinkingLoading] = useState(false);

    const [filters, setFilters] = useState({
        category: '',
        month: '',
        year: '',
        search: '',
        linked: '', // 'linked', 'unlinked', or '' for all
        source: '', // 'mpesa', 'bank', 'manual', or '' for all
    });

    const [searchInput, setSearchInput] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewingExpense, setViewingExpense] = useState(null);

    const isInitialMount = useRef(true);

    // Fetch clients for linking
    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_URL}/allclients`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClients(res.data[0]);
            console.log(res.data);
        } catch (error) {
            console.error('Error fetching clients:', error);
        }
    };

    // Fetch client transactions when a client is selected
    const fetchClientTransactions = async (clientId) => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_URL}/clients/${clientId}/transactions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClientTransactions(res.data[0]);
        } catch (error) {
            console.error('Error fetching client transactions:', error);
            setClientTransactions([]);
        }
    };

    // Apply filters to expenses
    const applyFilters = useCallback((expensesData, currentFilters) => {
        let filtered = [...expensesData];

        // Filter by search
        if (currentFilters.search) {
            const searchLower = currentFilters.search.toLowerCase();
            filtered = filtered.filter(exp =>
                exp.title?.toLowerCase().includes(searchLower) ||
                exp.description?.toLowerCase().includes(searchLower) ||
                exp.category?.toLowerCase().includes(searchLower)
            );
        }

        // Filter by category
        if (currentFilters.category) {
            filtered = filtered.filter(exp => exp.category === currentFilters.category);
        }

        // Filter by source
        if (currentFilters.source && currentFilters.source !== 'all') {
            filtered = filtered.filter(exp => exp.source === currentFilters.source);
        }

        // Filter by link status
        if (currentFilters.linked === 'linked') {
            filtered = filtered.filter(exp => exp.client_transaction_id !== null && exp.client_transaction_id !== undefined);
        } else if (currentFilters.linked === 'unlinked') {
            filtered = filtered.filter(exp => exp.client_transaction_id === null || exp.client_transaction_id === undefined);
        }

        // Filter by month
        if (currentFilters.month) {
            const [year, month] = currentFilters.month.split('-');
            filtered = filtered.filter(exp => {
                const expDate = new Date(exp.date);
                return expDate.getFullYear() === parseInt(year) &&
                    expDate.getMonth() === parseInt(month) - 1;
            });
        }

        // Filter by year
        if (currentFilters.year) {
            const year = parseInt(currentFilters.year);
            filtered = filtered.filter(exp => {
                const expDate = new Date(exp.date);
                return expDate.getFullYear() === year;
            });
        }

        return filtered;
    }, []);

    const fetchExpenses = useCallback(async (currentPage, currentRowsPerPage, currentFilters) => {
        setLoading(true);
        try {
            const params = {
                limit: 10000, // Fetch all expenses to filter on frontend
                offset: 0,
                // Only send filters that the backend supports
            };

            // Add search to params if it exists
            if (currentFilters.search) {
                params.search = currentFilters.search;
            }

            const response = await expenseApi.getUserExpenses(userId, params);
            const allExpenses = response.data.data.expenses || [];

            // Apply filters on frontend
            const filtered = applyFilters(allExpenses, currentFilters);
            setFilteredExpenses(filtered);

            // Paginate on frontend
            const start = currentPage * currentRowsPerPage;
            const end = start + currentRowsPerPage;
            const paginated = filtered.slice(start, end);

            setExpenses(paginated);
            setTotal(filtered.length);
        } catch (error) {
            console.error('Error fetching expenses:', error);
            toast.error('Failed to load expenses');
        } finally {
            setLoading(false);
        }
    }, [userId, applyFilters]);

    useEffect(() => {
        fetchClients();
    }, []);

    useEffect(() => {
        if (isInitialMount.current) return;

        const timer = setTimeout(() => {
            setFilters(prev => {
                if (prev.search === searchInput) return prev;
                return { ...prev, search: searchInput };
            });
            setPage(0);
        }, 400);

        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            fetchExpenses(page, rowsPerPage, filters);
            return;
        }
        fetchExpenses(page, rowsPerPage, filters);
    }, [page, rowsPerPage, filters, fetchExpenses]);

    const handleDelete = async () => {
        if (!selectedExpense) return;
        try {
            await expenseApi.deleteExpense(selectedExpense.id);
            toast.success('Expense deleted successfully');
            setDeleteDialogOpen(false);
            setSelectedExpense(null);
            fetchExpenses(page, rowsPerPage, filters);
        } catch (error) {
            console.error('Error deleting expense:', error);
            toast.error('Failed to delete expense');
        }
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;

        if (name === 'search') {
            setSearchInput(value);
            return;
        }

        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(0);
    };

    const clearFilters = () => {
        setSearchInput('');
        setFilters({
            category: '',
            month: '',
            year: '',
            search: '',
            linked: '',
            source: '',
        });
        setPage(0);
    };

    const formatDate = (dateString) => {
        try {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return formatDateFns(date, 'dd MMM yyyy');
        } catch (error) {
            console.error('Date formatting error:', error);
            return 'Invalid Date';
        }
    };

    const formatCurrency = (amount) => {
        return `KES ${parseFloat(amount).toLocaleString()}`;
    };

    const handleViewExpense = (expense) => {
        setViewingExpense(expense);
        setViewDialogOpen(true);
    };

    // Handle linking expense to transaction
    const handleLinkExpense = (expense) => {
        setLinkingExpense(expense);
        setSelectedClient(null);
        setSelectedTransaction(null);
        setClientTransactions([]);
        setLinkDialogOpen(true);
    };

    const handleClientChange = (event, newValue) => {
        setSelectedClient(newValue);
        if (newValue) {
            fetchClientTransactions(newValue.id);
        } else {
            setClientTransactions([]);
        }
        setSelectedTransaction(null);
    };

    const handleTransactionChange = (event, newValue) => {
        setSelectedTransaction(newValue);
    };

    const handleSaveLink = async () => {
        if (!linkingExpense || !selectedTransaction) {
            toast.error('Please select a client and transaction');
            return;
        }

        setLinkingLoading(true);
        try {
            const token = localStorage.getItem("token");
            await axios.put(`${API_URL}/api/expenses/${linkingExpense.id}/link`, {
                client_transaction_id: selectedTransaction.id,
                client_id: selectedClient.id
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Expense linked to transaction successfully');
            setLinkDialogOpen(false);
            setLinkingExpense(null);
            setSelectedClient(null);
            setSelectedTransaction(null);
            setClientTransactions([]);
            fetchExpenses(page, rowsPerPage, filters);
        } catch (error) {
            console.error('Error linking expense:', error);
            toast.error('Failed to link expense to transaction');
        } finally {
            setLinkingLoading(false);
        }
    };

    const handleUnlinkExpense = async (expenseId) => {
        if (!window.confirm('Are you sure you want to unlink this expense from the transaction?')) return;

        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_URL}/api/expenses/${expenseId}/link`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Expense unlinked successfully');
            fetchExpenses(page, rowsPerPage, filters);
        } catch (error) {
            console.error('Error unlinking expense:', error);
            toast.error('Failed to unlink expense');
        }
    };

    // Render category chip with icon and color
    const renderCategoryChip = (categoryValue) => {
        const category = getCategoryDetails(categoryValue);
        return (
            <Chip
                icon={<span style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}>
                    {category.icon}
                </span>}
                label={category.label}
                size="small"
                sx={{
                    backgroundColor: category.color + '20',
                    color: category.color,
                    borderColor: category.color,
                    '& .MuiChip-icon': {
                        color: category.color,
                    },
                }}
                variant="outlined"
            />
        );
    };

    // Render source chip
    const renderSourceChip = (sourceValue) => {
        const source = getSourceDetails(sourceValue);
        return (
            <Chip
                icon={<span style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                    {source.icon}
                </span>}
                label={source.label}
                size="small"
                sx={{
                    backgroundColor: source.color + '20',
                    color: source.color,
                    borderColor: source.color,
                    '& .MuiChip-icon': {
                        color: source.color,
                    },
                }}
                variant="outlined"
            />
        );
    };

    // Render link status
    const renderLinkStatus = (expense) => {
        if (expense.client_transaction_id) {
            return (
                <Chip
                    icon={<LinkIcon fontSize="small" />}
                    label={`Linked`}
                    size="small"
                    color="success"
                    variant="outlined"
                    onClick={() => handleViewExpense(expense)}
                />
            );
        }
        return (
            <Chip
                icon={<LinkOffIcon fontSize="small" />}
                label="Unlinked"
                size="small"
                color="warning"
                variant="outlined"
            />
        );
    };

    const debugExport = () => {
        console.log('=== EXPORT DEBUG ===');
        console.log('Expenses state:', expenses);
        console.log('Number of expenses:', expenses.length);

        if (expenses.length === 0) {
            toast.warning('No expenses to export');
            return;
        }

        console.log('First expense sample:', expenses[0]);
        toast.success(`Found ${expenses.length} expenses ready for export`);
    };

    const handleExport = async (formatType = 'excel') => {
        if (expenses.length === 0) {
            toast.error('No data to export');
            return;
        }

        setExporting(true);

        try {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            const filename = `expenses_${timestamp}`;
            let exportResult = false;

            switch (formatType) {
                case 'excel':
                    exportResult = ExportUtils.exportToExcel(expenses, filename);
                    break;
                case 'pdf':
                    exportResult = ExportUtils.exportToPDF(expenses, 'Expense List Report');
                    break;
                case 'csv':
                    exportResult = ExportUtils.exportToCSV(expenses, filename);
                    break;
                default:
                    toast.error('Invalid export format');
            }

            if (exportResult) {
                toast.success(`${formatType.toUpperCase()} exported successfully! ${expenses.length} records`);
            } else {
                toast.error('Export failed');
            }

        } catch (error) {
            console.error('Export error:', error);
            toast.error(`Export failed: ${error.message || 'Unknown error'}`);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: { xs: 2, sm: 0 }
            }}>
                <Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'}>
                        Expense Management
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        Manage and track all your expenses
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Tooltip title="Refresh">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={() => fetchExpenses(page, rowsPerPage, filters)}
                                size="small"
                            >
                                {!isMobile && 'Refresh'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Export to Excel">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={<Description />}
                                onClick={() => handleExport('excel')}
                                size="small"
                                disabled={exporting || expenses.length === 0}
                                color="success"
                            >
                                {!isMobile && 'Excel'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Export to PDF">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={<PictureAsPdf />}
                                onClick={() => handleExport('pdf')}
                                size="small"
                                disabled={exporting || expenses.length === 0}
                                color="error"
                            >
                                {!isMobile && 'PDF'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title="Add new expense">
                        <span>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => window.location.href = '/admin/expenses/new'}
                                color="primary"
                                size="small"
                            >
                                {!isMobile ? 'Add Expense' : 'Add'}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {exporting && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <LinearProgress sx={{ mt: 1 }} />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        Exporting {expenses.length} records...
                    </Typography>
                </Alert>
            )}

            {/* Filters */}
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={2}>
                        <TextField
                            fullWidth
                            placeholder="Search expenses..."
                            name="search"
                            value={searchInput}
                            onChange={handleFilterChange}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                name="category"
                                value={filters.category}
                                onChange={handleFilterChange}
                                input={<OutlinedInput label="Category" />}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {EXPENSE_CATEGORIES.map((cat) => (
                                    <MenuItem key={cat.value} value={cat.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span style={{ color: cat.color }}>{cat.icon}</span>
                                            {cat.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Source</InputLabel>
                            <Select
                                name="source"
                                value={filters.source}
                                onChange={handleFilterChange}
                                input={<OutlinedInput label="Source" />}
                            >
                                {SOURCE_OPTIONS.map((src) => (
                                    <MenuItem key={src.value} value={src.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span style={{ color: src.color }}>{src.icon}</span>
                                            {src.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Link Status</InputLabel>
                            <Select
                                name="linked"
                                value={filters.linked}
                                onChange={handleFilterChange}
                                input={<OutlinedInput label="Link Status" />}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="linked">Linked to Transaction</MenuItem>
                                <MenuItem value="unlinked">Unlinked</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            fullWidth
                            label="Month"
                            name="month"
                            type="month"
                            value={filters.month}
                            onChange={handleFilterChange}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={clearFilters}
                            startIcon={<Clear />}
                            size="medium"
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Expenses Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                Description
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                Date
                            </TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Link Status</TableCell>
                            <TableCell align="center" sx={{ minWidth: 120 }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : expenses.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Box sx={{ py: 4 }}>
                                        <Typography color="textSecondary" gutterBottom>
                                            No expenses found
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Try adjusting your filters or add a new expense
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            expenses.map((expense) => (
                                <TableRow key={expense.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="500">
                                            {expense.title}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {renderCategoryChip(expense.category)}
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {expense.description || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Typography variant="body2">
                                            {formatDate(expense.date)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                            {formatCurrency(expense.amount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {renderSourceChip(expense.source || 'manual')}
                                    </TableCell>
                                    <TableCell>
                                        {renderLinkStatus(expense)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={0.5} justifyContent="center">
                                            <Tooltip title="View">
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => handleViewExpense(expense)}
                                                >
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => window.location.href = `/admin/expenses/edit/${expense.id}`}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {expense.client_transaction_id ? (
                                                <Tooltip title="Unlink from Transaction">
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        onClick={() => handleUnlinkExpense(expense.id)}
                                                    >
                                                        <LinkOffIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Link to Transaction">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => handleLinkExpense(expense)}
                                                    >
                                                        <LinkIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setSelectedExpense(expense);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </TableContainer>

            {/* Link Expense to Transaction Dialog */}
            <Dialog
                open={linkDialogOpen}
                onClose={() => {
                    setLinkDialogOpen(false);
                    setLinkingExpense(null);
                    setSelectedClient(null);
                    setSelectedTransaction(null);
                    setClientTransactions([]);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Link Expense to Transaction
                            {linkingExpense && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {linkingExpense.title} - {formatCurrency(linkingExpense.amount)}
                                </Typography>
                            )}
                        </Typography>
                        <IconButton onClick={() => {
                            setLinkDialogOpen(false);
                            setLinkingExpense(null);
                            setSelectedClient(null);
                            setSelectedTransaction(null);
                            setClientTransactions([]);
                        }} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Select Client
                        </Typography>
                        <Autocomplete
                            fullWidth
                            options={clients}
                            getOptionLabel={(option) => `${option.contact_name} (${option.company_name || 'N/A'})`}
                            value={selectedClient}
                            onChange={handleClientChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search Client"
                                    placeholder="Type to search..."
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            sx={{ mb: 2 }}
                        />

                        {selectedClient && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>
                                    Select Transaction
                                </Typography>
                                <Autocomplete
                                    fullWidth
                                    options={clientTransactions}
                                    getOptionLabel={(option) => {
                                        const services = Array.isArray(option.nature_of_service)
                                            ? option.nature_of_service.join(', ')
                                            : option.nature_of_service || 'N/A';
                                        return `${services} - ${formatCurrency(option.quotation_amount)}`;
                                    }}
                                    value={selectedTransaction}
                                    onChange={handleTransactionChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Transaction"
                                            placeholder="Type to search..."
                                            variant="outlined"
                                            size="small"
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body2">
                                                    {Array.isArray(option.nature_of_service)
                                                        ? option.nature_of_service.join(', ')
                                                        : option.nature_of_service || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatCurrency(option.quotation_amount)} - {formatDate(option.date)}
                                                </Typography>
                                            </Box>
                                        </li>
                                    )}
                                />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => {
                        setLinkDialogOpen(false);
                        setLinkingExpense(null);
                        setSelectedClient(null);
                        setSelectedTransaction(null);
                        setClientTransactions([]);
                    }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveLink}
                        disabled={!selectedTransaction || linkingLoading}
                        startIcon={linkingLoading ? <CircularProgress size={20} /> : <LinkIcon />}
                    >
                        {linkingLoading ? 'Linking...' : 'Link to Transaction'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Expense Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                TransitionComponent={isMobile ? Slide : Fade}
                fullScreen={isMobile}
            >
                {viewingExpense && (
                    <>
                        <DialogTitle>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6">Expense Details</Typography>
                                <IconButton onClick={() => setViewDialogOpen(false)} size="small">
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <Divider />
                        <DialogContent>
                            <Box sx={{ mt: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                                        <Typography variant="body1" fontWeight="500">
                                            {viewingExpense.title}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                                        {renderCategoryChip(viewingExpense.category)}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                                        <Typography variant="body1">
                                            {viewingExpense.description || 'No description provided'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                                        <Typography variant="body1">
                                            {formatDate(viewingExpense.date)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                                        <Typography variant="body1" fontWeight="bold" color="primary">
                                            {formatCurrency(viewingExpense.amount)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                                        <Typography variant="body1">
                                            {viewingExpense.payment_method || 'Mobile Money'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Source</Typography>
                                        {renderSourceChip(viewingExpense.source || 'manual')}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Link Status</Typography>
                                        {renderLinkStatus(viewingExpense)}
                                    </Grid>
                                    {viewingExpense.client_transaction_id && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary">Linked Transaction</Typography>
                                            <Typography variant="body1">
                                                {viewingExpense.client_name || `Transaction #${viewingExpense.client_transaction_id}`}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {viewingExpense.is_recurring && (
                                        <Grid item xs={12}>
                                            <Chip
                                                label="Recurring"
                                                color="primary"
                                                size="small"
                                                icon={<Subscriptions />}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    setViewDialogOpen(false);
                                    window.location.href = `/admin/expenses/edit/${viewingExpense.id}`;
                                }}
                            >
                                Edit
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Expense</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{selectedExpense?.title}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExpenseList;